import lazyInit from 'util/lazyInit';

export interface ModelFetcher {
  fetchMulti(keys: Array<string>): Promise<void>;
  isKeyLoadedOrLoading(key: string): boolean;
}

/**
 * Code for dealing with fetching models in an autobatched way.
 *
 * The Autobatcher coalesces multiple requests to load data from the server of the same type into
 * bulk requests.
 *
 * This file exposes a single function, requestFetch(modelFetcher, modelKey), which callers can
 * use to indicate that they want the given resource to be loaded (but they are delegating the exact
 * specifics of that loading to the framework). Autobatcher keeps track of all of these requests in
 * pendingFetchesByClass, then periodically makes one bulk request per type of resource for all
 * collected keys of that resource (excutedBatchedRequestsForPendingFetches).
 *
 * ModelFetchers are classes that contain the data management of a certain type of model. Each
 * ModelFetcher should expose a requestFetch(modelId) and a fetchMulti(modelIds) to be used with
 * Autobatcher.
 *
 * The Autobatcher will not honor a requestFetch if any of the following is true:
 *   - it's already loaded in the store
 *   - there's already plans to fetch it (e.g., it's already inside pendingFetchesByClass)
 *   - it's already being fetch (a request for it is in flight)
 */
class Autobatcher {
  pendingFetchesByClass: Map<ModelFetcher, Array<string>> = new Map();
  activeFetchesByClass: Map<ModelFetcher, Array<string>> = new Map();
  areBatchedRequestsScheduled: boolean = false;
  private scheduledBatchTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.reset();
  }

  /**
   * Clears pending and active fetch bookkeeping. It also cancels any scheduled batch, so pending keys
   * requested before the reset are not fetched. In-flight fetches are not canceled.
   */
  reset(): void {
    if (this.scheduledBatchTimeout != null) {
      clearTimeout(this.scheduledBatchTimeout);
      this.scheduledBatchTimeout = null;
    }
    this.pendingFetchesByClass = new Map();
    this.activeFetchesByClass = new Map();
    // Indicates whether we should schedule a new set of batched requests (via
    // performPendingFetches)
    this.areBatchedRequestsScheduled = false;
  }

  isFetchPending(modelFetcher: ModelFetcher, modelKey: string): boolean {
    const pendingFetches = this.pendingFetchesByClass.get(modelFetcher);
    return Boolean(pendingFetches && pendingFetches.includes(modelKey));
  }

  isFetchActive(modelFetcher: ModelFetcher, modelKey: string): boolean {
    const activeFetches = this.activeFetchesByClass.get(modelFetcher);
    return Boolean(activeFetches && activeFetches.includes(modelKey));
  }

  isFetchPendingOrActive(modelFetcher: ModelFetcher, modelKey: string): boolean {
    return this.isFetchPending(modelFetcher, modelKey) || this.isFetchActive(modelFetcher, modelKey);
  }

  addPendingFetch(modelFetcher: ModelFetcher, modelKey: string): void {
    let pendingFetches = this.pendingFetchesByClass.get(modelFetcher);
    if (!pendingFetches) {
      pendingFetches = [];
    }
    if (!this.isFetchPending(modelFetcher, modelKey) && !this.isFetchActive(modelFetcher, modelKey)) {
      pendingFetches.push(modelKey);
      this.pendingFetchesByClass.set(modelFetcher, pendingFetches);
      if (!this.areBatchedRequestsScheduled) {
        this.scheduledBatchTimeout = setTimeout(this.performPendingFetches);
      }
      this.areBatchedRequestsScheduled = true;
    }
  }

  performPendingFetches = (): void => {
    this.scheduledBatchTimeout = null;
    const pendingFetchesToPerform = this.pendingFetchesByClass;
    this.pendingFetchesByClass = new Map();
    this.areBatchedRequestsScheduled = false;
    for (const [modelFetcher, modelKeys] of pendingFetchesToPerform.entries()) {
      const oldActiveFetches = this.activeFetchesByClass.get(modelFetcher)
        ? this.activeFetchesByClass.get(modelFetcher)!
        : [];
      this.activeFetchesByClass.set(modelFetcher, [...oldActiveFetches, ...modelKeys]);
      Promise.resolve(modelFetcher.fetchMulti(modelKeys)).finally(() => {
        const oldModelKeys = this.activeFetchesByClass.get(modelFetcher);
        const newModelKeys = oldModelKeys ? oldModelKeys.filter((key) => !modelKeys.includes(key)) : [];
        this.activeFetchesByClass.set(modelFetcher, newModelKeys);
      });
    }
  };
}

export const getAutobatcher = lazyInit<Autobatcher>(
  () => new Autobatcher(),
  (autobatcher) => {
    autobatcher.reset();
  }
);

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface requestFetchOptions {
  forceFetch: boolean;
}

/**
 * Add this model to the list of models to fetch asynchronously if they're not already in the store
 * or in the process of being fetched.
 */
export function requestFetch(
  modelFetcher: ModelFetcher,
  modelKey: string,
  {forceFetch}: requestFetchOptions = {forceFetch: false}
): void {
  // Ideally we would avoid fetching if the model is currently being fetched ("loading"), but currently
  // `isKeyLoadedOrLoading` combines the loaded and loading states
  if (!forceFetch && modelFetcher.isKeyLoadedOrLoading(modelKey)) {
    // Don't add a pending fetch if the model manager reports that the key's already loaded or
    // already loading (request in flight)
    return;
  }
  getAutobatcher().addPendingFetch(modelFetcher, modelKey);
}
